<template>
  <v-app id="app">
    <TheHeader v-if="showAppShell" />
    <TheSideMenu v-if="showAppShell" />
    <v-main>
      <!-- Full-width 2FA reminder band. Lives inside v-main (the content column)
           rather than the app-bar extension so it offsets only the content — the
           clipped side drawer stays pinned under the toolbar instead of being
           pushed down by the extension height. -->
      <TwoFactorRibbon
        v-if="showAppShell && !hasBypassTwoFactor"
        :two-factor-required="twoFactorRequired"
        :skip-two-factor-until="skipTwoFactorUntil"
      />
      <v-container
        :class="{
          'fill-height': !authenticated || isMobileInventoryPage || isAuthPage,
          'pa-0': isMobileInventoryPage,
        }"
        :fluid="!isMobileInventoryPage || true"
      >
        <LayoutPageSkeleton v-if="pendingReload" />
        <router-view v-else />
      </v-container>
    </v-main>
    <TheFooter v-if="!isMobileInventoryPage" />
    <GlobalBulkNotificationPopup v-if="showAppShell" />
  </v-app>
</template>

<script>
import "./scss/app.scss";
import TheHeader from "@layout/TheHeader.vue";
import TheFooter from "@layout/TheFooter.vue";
import TheSideMenu from "@layout/TheSideMenu.vue";
import LayoutPageSkeleton from "@layout/LayoutPageSkeleton.vue";
import GlobalBulkNotificationPopup from "./components/notifications/GlobalBulkNotificationPopup.vue";
import TwoFactorRibbon from "@components/authentication/TwoFactorRibbon.vue";
import useUser from "./components/authentication/useUser";
import useFacility from "./components/authentication/useFacility";
import useAccessibleScope from "./components/authentication/useAccessibleScope";
import useDarkMode from "./composables/useDarkMode";
import { computed } from "vue";
import { useRoute } from "vue-router/composables";

export default {
  name: "App",
  components: {
    TheHeader,
    TheFooter,
    TheSideMenu,
    LayoutPageSkeleton,
    GlobalBulkNotificationPopup,
    TwoFactorRibbon,
  },
  setup() {
    const route = useRoute();
    const {
      authenticated,
      defaultOrganization,
      restoreCurrentUser,
      currentUser,
    } = useUser();
    const {
      currentFacilityId,
      redirectToDashboard,
      selectFacility,
      restoreCurrentFacilityId,
    } = useFacility();
    const { singleFacilityScope } = useAccessibleScope();
    const { isDark } = useDarkMode();

    const isMobileInventoryPage = computed(() => {
      return route.path === "/mobile-inventory" || !!route.meta?.standalone;
    });

    // Auth pages (login, 2FA, change-password, invite) center their card in the
    // viewport. Keying only on `!authenticated` broke this when a stale session
    // lingered (e.g. opening a reset link while still logged in) — the container
    // lost fill-height and the card jumped to the top.
    const isAuthPage = computed(() => !!route.meta?.withoutAuth);

    // Render the authenticated app chrome (header, sidebar, ribbon, popups) only
    // on real in-app routes. Auth pages are `withoutAuth`; during 2FA setup the
    // token/user get persisted (authenticated flips true) a moment before the
    // hard redirect fires, and without this the shell would flash over the still
    // -mounted setup card. Gating on the route keeps those pages standalone.
    const showAppShell = computed(
      () =>
        authenticated.value &&
        !isMobileInventoryPage.value &&
        !route.meta?.withoutAuth
    );

    // 2FA reminder inputs (previously in TheHeader). The band renders in the
    // content column now, so these live where it's mounted.
    const hasBypassTwoFactor = computed(() => {
      if (!currentUser.value?.roles) return false;
      return currentUser.value.roles.some(
        (orgRole) =>
          orgRole.role &&
          orgRole.organizationId ===
            currentUser.value.defaultOrganization?.id &&
          orgRole.role.bypassTwoFactor
      );
    });
    const twoFactorRequired = computed(() =>
      Boolean(currentUser.value?.twoFactorMethod)
    );
    const skipTwoFactorUntil = computed(
      () => currentUser.value?.skipTwoFactorUntil
    );

    if (!authenticated.value && !isMobileInventoryPage.value) {
      restoreCurrentUser();
    }

    return {
      authenticated,
      defaultOrganization,
      singleFacilityScope,
      selectFacility,
      currentFacilityId,
      redirectToDashboard,
      restoreCurrentFacilityId,
      isDark,
      isMobileInventoryPage,
      isAuthPage,
      showAppShell,
      hasBypassTwoFactor,
      twoFactorRequired,
      skipTwoFactorUntil,
    };
  },
  computed: {
    pendingReload() {
      //TODO: Make global property directly reactive
      return this.$pendingReload.value;
    },
  },
  watch: {
    isDark: {
      handler(newValue) {
        this.$vuetify.theme.dark = newValue;
      },
      immediate: true,
    },
    redirectToDashboard(val) {
      if (val) {
        this.$router.replace({
          name: "FacilityDashboard",
          params: { organization_id: this.currentFacilityId },
        });
      }
    },
  },
  beforeMount() {
    if (this.authenticated && !this.isMobileInventoryPage) {
      // The navigable scope is already derived from the restored user's roles
      // (restoreCurrentUser), so there's no scope fetch here — just seed the
      // facility: a single-facility user enters their facility; anyone
      // else restores their last-selected facility (or the routed one).
      const facility = this.singleFacilityScope
        ? this.singleFacilityScope
        : {
            id: this.restoreCurrentFacilityId(
              this.$route?.params?.organization_id
            ),
          };

      this.selectFacility(this.$apollo, facility);
    }
  },
};
</script>
